/**
 * SplashMessageBox — rotating announcement banner shown at the top of Browse.
 *
 * • Auto-rotates every 8s (paused on hover).
 * • Prev / Next arrows + dot pagination.
 * • Fade transition between messages.
 * • Renders admin-authored rich text (already sanitized server-side) via
 *   DOMPurify as a defense-in-depth measure.
 * • Charcoal surface, orange accents — no primary green — matches Hunt57
 *   design language.
 * • Renders nothing when no active messages exist.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const ROTATION_MS = 8000;

// DOMPurify config — matches the backend bleach whitelist. Any tag not in
// this list gets stripped; anchors are forced to open with safe rel attrs
// when target=_blank.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: ["b", "i", "u", "strong", "em", "a", "br", "p", "ul", "ol", "li", "span", "div", "h3", "h4", "s", "code"],
  ALLOWED_ATTR: ["href", "title", "target", "rel", "style"],
};

function sanitize(html) {
  if (!html) return "";
  const clean = DOMPurify.sanitize(html, PURIFY_CONFIG);
  // Force safe rel on any anchor that opens a new window.
  return clean.replace(/<a\s+([^>]*target=["']_blank["'][^>]*)>/gi, (_m, attrs) => {
    if (/rel\s*=/.test(attrs)) return `<a ${attrs}>`;
    return `<a ${attrs} rel="noopener noreferrer">`;
  });
}

function ExternalLinkIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="inline-block ml-1">
      <path d="M7 17L17 7M17 7H8M17 7v9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function SplashMessageBox({ testId = "browse-splash" }) {
  const [messages, setMessages] = useState([]);
  const [index, setIndex] = useState(0);
  const [fadeKey, setFadeKey] = useState(0);
  const hoverRef = useRef(false);
  const timerRef = useRef(null);

  // Fetch active messages once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/announcements/active");
        if (!cancelled) setMessages(data?.messages || []);
      } catch {
        /* silent — banner just won't render */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate every 8 seconds. Pause when the user hovers the banner.
  useEffect(() => {
    if (messages.length <= 1) return undefined;
    const tick = () => {
      if (hoverRef.current) return;
      setIndex((i) => (i + 1) % messages.length);
      setFadeKey((k) => k + 1);
    };
    timerRef.current = setInterval(tick, ROTATION_MS);
    return () => clearInterval(timerRef.current);
  }, [messages.length]);

  const current = messages[index];
  const cleanHtml = useMemo(() => sanitize(current?.content), [current]);

  if (!messages.length || !current) return null;

  const goto = (i) => {
    setIndex(((i % messages.length) + messages.length) % messages.length);
    setFadeKey((k) => k + 1);
  };
  const prev = () => goto(index - 1);
  const next = () => goto(index + 1);

  const isExternal = current.cta_link && /^https?:\/\//i.test(current.cta_link);

  return (
    <div
      data-testid={testId}
      className="mb-4 relative overflow-hidden border border-white/10 bg-[#0f0f0f]"
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; }}
    >
      {/* subtle orange stripe on the left */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#FF5F1F] via-[#FFD700] to-[#FF5F1F]" aria-hidden="true" />

      <div className="flex items-stretch gap-3 px-4 sm:px-6 py-4 sm:py-5">
        {/* Prev arrow (only when >1) */}
        {messages.length > 1 && (
          <button
            type="button"
            onClick={prev}
            data-testid={`${testId}-prev`}
            aria-label="Previous message"
            className="hidden sm:flex items-center justify-center w-7 h-7 shrink-0 self-center rounded-full border border-white/10 text-zinc-400 hover:text-white hover:border-white/40 transition"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
        )}

        {/* Message body — keyed so fade animation replays on rotate */}
        <div key={fadeKey} className="flex-1 min-w-0 h57-splash-fade">
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <div
              data-testid={`${testId}-title`}
              className="font-mono text-[10px] tracking-widest uppercase text-[#FF5F1F] truncate"
            >
              → {current.title || "Announcement"}
            </div>
            {messages.length > 1 && (
              <div className="font-mono text-[9px] tracking-widest uppercase text-zinc-500 shrink-0">
                {index + 1} / {messages.length}
              </div>
            )}
          </div>

          <div
            data-testid={`${testId}-body`}
            className="h57-splash-body text-sm text-zinc-200 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: cleanHtml }}
          />

          {current.cta_text && current.cta_link && (
            <div className="mt-3">
              <a
                href={current.cta_link}
                target={current.cta_new_tab ? "_blank" : undefined}
                rel={current.cta_new_tab ? "noopener noreferrer" : undefined}
                data-testid={`${testId}-cta`}
                className="inline-flex items-center h57-btn-primary !py-2 !px-4 !text-xs"
              >
                {current.cta_text}
                {current.cta_new_tab && isExternal && <ExternalLinkIcon />}
              </a>
            </div>
          )}
        </div>

        {/* Next arrow */}
        {messages.length > 1 && (
          <button
            type="button"
            onClick={next}
            data-testid={`${testId}-next`}
            aria-label="Next message"
            className="hidden sm:flex items-center justify-center w-7 h-7 shrink-0 self-center rounded-full border border-white/10 text-zinc-400 hover:text-white hover:border-white/40 transition"
          >
            <CaretRight size={14} weight="bold" />
          </button>
        )}
      </div>

      {/* Mobile arrows + dots row */}
      {messages.length > 1 && (
        <div className="flex items-center justify-between px-4 pb-3 sm:pb-4">
          <div className="flex gap-1 sm:hidden">
            <button
              type="button"
              onClick={prev}
              data-testid={`${testId}-prev-mobile`}
              aria-label="Previous"
              className="w-7 h-7 flex items-center justify-center rounded-full border border-white/10 text-zinc-400"
            >
              <CaretLeft size={12} weight="bold" />
            </button>
            <button
              type="button"
              onClick={next}
              data-testid={`${testId}-next-mobile`}
              aria-label="Next"
              className="w-7 h-7 flex items-center justify-center rounded-full border border-white/10 text-zinc-400"
            >
              <CaretRight size={12} weight="bold" />
            </button>
          </div>

          <div className="flex-1 flex justify-center gap-1.5">
            {messages.map((m, i) => (
              <button
                key={m.id}
                type="button"
                onClick={() => goto(i)}
                data-testid={`${testId}-dot-${i}`}
                aria-label={`Go to message ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-[#FF5F1F]" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
                style={{ borderRadius: "9999px" }}
              />
            ))}
          </div>

          <div className="w-14 sm:hidden" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

/**
 * SplashMessagePreview — non-fetching preview used by the admin console.
 * Accepts a single message object and renders it exactly like the live
 * banner (minus rotation / dots).
 */
export function SplashMessagePreview({ message, testId = "splash-preview" }) {
  const cleanHtml = useMemo(() => sanitize(message?.content), [message]);
  if (!message) return null;
  const isExternal = message.cta_link && /^https?:\/\//i.test(message.cta_link);
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden border border-white/10 bg-[#0f0f0f]"
    >
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#FF5F1F] via-[#FFD700] to-[#FF5F1F]" aria-hidden="true" />
      <div className="px-4 sm:px-6 py-4 sm:py-5">
        <div className="font-mono text-[10px] tracking-widest uppercase text-[#FF5F1F] mb-1">
          → {message.title || "Announcement"}
        </div>
        <div
          className="h57-splash-body text-sm text-zinc-200 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
        {message.cta_text && message.cta_link && (
          <div className="mt-3">
            <a
              href={message.cta_link}
              target={message.cta_new_tab ? "_blank" : undefined}
              rel={message.cta_new_tab ? "noopener noreferrer" : undefined}
              className="inline-flex items-center h57-btn-primary !py-2 !px-4 !text-xs"
              onClick={(e) => e.preventDefault()}
            >
              {message.cta_text}
              {message.cta_new_tab && isExternal && <ExternalLinkIcon />}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
